import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';
import {
  BasePage,
  MobileScreen,
  TabletScreen,
  StaticConsumer
} from '@sm/webassets';
import {
  Typography,
  Card,
  Grid,
  Col,
  Row,
  Button,
  Container
} from '@sm/wds-react';
import { generateMetricsAttribute } from '@sm/metrics';
import objectFitImages from 'object-fit-images';

import './solutions.scss';
import modulesQuery from './solutions.graphql';
import Carousel from './components/Carousel';
import { johnsonJohnson, box, gopro, lyft, progressive } from './static/images';

const SolutionButton = ({ href, color, variant, children }) => (
  <Button
    href={href}
    data-sm-metrics={generateMetricsAttribute({
      data: {
        actionFlow: 'marketplace_modal',
        actionType: 'button_click',
        otherData: {
          page: 'marketplace'
        }
      }
    })}
    stretched
    variant={variant}
    color={color}
    className="wds-m-b-3"
  >
    {children}
  </Button>
);
SolutionButton.propTypes = {
  href: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  variant: PropTypes.string
};
SolutionButton.defaultProps = {
  variant: null
};

const isSolutionReadyForEap = subtype =>
  ['AD', 'PACKAGE', 'NAME', 'LOGO', 'MESSAGE'].includes(subtype);

const solutionCard = (solution, eap) => (
  <Card className="sm-solutions__solution wds-m-b-6" padding={false}>
    <div className="sm-solutions__solution-image-wrapper">
      <img
        src={solution.image}
        alt={solution.title}
        className="sm-solutions__solution-image"
      />
    </div>
    <div className="wds-flex__item--grow sm-solutions__solution-header">
      <Typography
        className="wds-flex__item--grow sm-solutions__solution-header-title"
        variant="cardTitle"
      >
        {solution.title}
      </Typography>
      <Typography className="wds-flex__item--grow sm-solutions__solution-header-tagline">
        {solution.tagline}
      </Typography>
    </div>
    <Container mt={5}>
      <Typography variant="bodySm" component="span">
        Starting at&nbsp;
      </Typography>
      <Typography component="span" className="sm-solutions__solution-price">
        {`$${new Intl.NumberFormat(solution.price.locale, {
          style: 'decimal',
          currency: 'USD',
          unitDisplay: 'long'
        }).format(solution.price.cost)}`}
      </Typography>
    </Container>
    <Grid className="wds-m-t-4" noBleeds>
      <Row>
        <Col xl={6} lg={12} md={6} xs={12}>
          <Choose>
            <When condition={eap && isSolutionReadyForEap(solution.subtype)}>
              <SolutionButton
                href={`/solutions/create/${solution.id}`}
                color="upgrade"
              >
                Get started
              </SolutionButton>
            </When>
            <Otherwise>
              <SolutionButton
                href="http://www.surveymonkey.com/welcome/market-research-solutions-eap-signup/"
                color="upgrade"
              >
                Contact us
              </SolutionButton>
            </Otherwise>
          </Choose>
        </Col>
        <Col xl={6} lg={12} md={6} xs={12}>
          <SolutionButton
            variant="ghost"
            color="secondary"
            href="http://www.surveymonkey.com/welcome/market-research-solutions-eap-signup/"
          >
            Learn more
          </SolutionButton>
        </Col>
      </Row>
    </Grid>
  </Card>
);

const AnimatedSolution = ({ delay, children }) => {
  const [startAnimation, setStartAnimation] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setStartAnimation(true);
    }, delay);
    return () => {
      clearTimeout(timeout);
    };
  }, [delay]);

  const classList = classnames('sm-solutions__solution-animated-container', {
    'sm-solutions__solution-animated-container--started': startAnimation
  });

  return <div className={classList}>{children}</div>;
};
AnimatedSolution.propTypes = {
  delay: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired
};

const FooterLogo = ({ alt, src }) => (
  <img alt={alt} src={src} className="sm-solutions__footer-logo" />
);
FooterLogo.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired
};

const Solutions = ({ location: { search } }) => {
  useEffect(() => {
    objectFitImages('img.sm-solutions__solution-image');
  });
  const eap = new URLSearchParams(search).get('eap') === 'true';
  return (
    <StaticConsumer>
      {({ user }) => {
        const heroClassnames = classnames('sm-solutions__hero', {
          'sm-solutions__hero--auth': user.isAuthenticated
        });
        const heroTextWrapperClassnames = classnames(
          'sm-solutions__hero-text-wrapper',
          {
            'sm-solutions__hero-text-wrapper--auth': user.isAuthenticated
          }
        );
        return (
          <BasePage
            color="transparent"
            includeHeader
            includeFooter
            pageId="Market Research - Solutions"
            legacyWeb=""
          >
            <div className="sm-solutions">
              <Container
                className={heroClassnames}
                pr={5}
                pl={5}
                pt={6}
                flex
                flexDirection="column"
                flexJustify="start"
                flexAlign="center"
              >
                <div className={heroTextWrapperClassnames}>
                  <Typography
                    color="light"
                    variant="cardTitle"
                    className="sm-solutions__hero-name"
                  >
                    SURVEYMONKEY MARKET RESEARCH SOLUTIONS
                  </Typography>
                  <Typography
                    color="light"
                    variant="hero1"
                    component="h1"
                    className="sm-solutions__hero-tagline wds-m-t-3"
                  >
                    <strong>Automate your market research</strong>
                  </Typography>
                  <Typography
                    color="light"
                    variant="cardTitle"
                    className="sm-solutions__hero-description wds-m-t-3"
                  >
                    Get polished, presentation-ready insights—in as little as an
                    hour!
                    <br />
                    Our solutions help build your survey based on expert
                    methodology, target your audience, and analyze your data for
                    you.
                  </Typography>
                </div>
              </Container>
              <Query query={modulesQuery}>
                {({ loading, error, data }) => (
                  <Choose>
                    <When condition={loading}>Loading</When>
                    <When condition={error}>We failed miserably</When>
                    <Otherwise>
                      <>
                        <div>
                          <MobileScreen>
                            <Carousel className="sm-solutions__solution-container--mobile">
                              {data.modules.items

                                .filter(s => !!s.title && !!s.tagline) // TODO: remove when we have better data
                                .map(solution => (
                                  <div key={solution.id}>
                                    {solutionCard(solution, eap)}
                                  </div>
                                ))}
                            </Carousel>
                          </MobileScreen>
                          <TabletScreen>
                            <Grid className="sm-solutions__solution-container wds-m-b-8">
                              <Row>
                                {data.modules.items
                                  .filter(s => !!s.title && !!s.tagline) // TODO: remove when we have better data
                                  .map((solution, index) => (
                                    <Col
                                      xs={12}
                                      sm={12}
                                      md={6}
                                      lg={4}
                                      key={solution.id}
                                    >
                                      <AnimatedSolution
                                        delay={1000 + (index + 1) * 200}
                                      >
                                        {solutionCard(solution, eap)}
                                      </AnimatedSolution>
                                    </Col>
                                  ))}
                              </Row>
                            </Grid>
                          </TabletScreen>
                        </div>
                        <Container
                          className="sm-solutions__footer"
                          px={4}
                          py={8}
                        >
                          <Grid>
                            <Row>
                              <Col xs={2} />
                              <Col xs={8}>
                                <Typography
                                  variant="sectionTitle"
                                  className="sm-solutions__footer-title"
                                >
                                  SurveyMonkey is used in more than 335,000
                                  organizations worldwide
                                </Typography>
                              </Col>
                              <Col xs={2} />
                            </Row>
                          </Grid>
                          <Grid className="wds-m-t-4">
                            <Row className="sm-solutions__footer-logos-row">
                              <Col sm={4} lg={2}>
                                <FooterLogo
                                  alt="Johnson & Johnson"
                                  src={johnsonJohnson}
                                />
                              </Col>
                              <Col sm={4} lg={2}>
                                <FooterLogo alt="box" src={box} />
                              </Col>
                              <Col sm={4} lg={2}>
                                <FooterLogo alt="GoPro" src={gopro} />
                              </Col>
                              <Col sm={6} lg={2}>
                                <FooterLogo alt="lyft" src={lyft} />
                              </Col>
                              <Col sm={6} lg={2}>
                                <FooterLogo
                                  alt="Progressive"
                                  src={progressive}
                                />
                              </Col>
                            </Row>
                          </Grid>
                        </Container>
                      </>
                    </Otherwise>
                  </Choose>
                )}
              </Query>
            </div>
          </BasePage>
        );
      }}
    </StaticConsumer>
  );
};

Solutions.propTypes = {
  location: PropTypes.shape({ search: PropTypes.string }).isRequired
};

export default withRouter(Solutions);
